import { getBackendUrl } from '../config/environment';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Trash2, Star, Filter, AlertCircle, TrendingUp } from 'lucide-react';

const TestimonialsManagement = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');

  const backendUrl = getBackendUrl();

  // Fetch testimonials and stats
  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all testimonials
      const testimonialsResponse = await fetch(`${backendUrl}/api/testimonials/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Fetch stats
      const statsResponse = await fetch(`${backendUrl}/api/testimonials/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (testimonialsResponse.ok && statsResponse.ok) {
        const testimonialsData = await testimonialsResponse.json();
        const statsData = await statsResponse.json();
        
        setTestimonials(testimonialsData);
        setStats(statsData);
      } else {
        setError('Erreur lors du chargement des données');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Approve testimonial
  const handleApprove = async (id) => {
    setActionLoading({ ...actionLoading, [id]: 'approving' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/testimonials/admin/${id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchData();
      } else {
        setError('Erreur lors de l\'approbation');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading({ ...actionLoading, [id]: null });
    }
  };

  // Reject testimonial
  const handleReject = async (id) => {
    setActionLoading({ ...actionLoading, [id]: 'rejecting' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/testimonials/admin/${id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchData();
      } else {
        setError('Erreur lors du refus');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading({ ...actionLoading, [id]: null });
    }
  };

  // Delete testimonial
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce témoignage ?')) {
      return;
    }

    setActionLoading({ ...actionLoading, [id]: 'deleting' });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/testimonials/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchData();
      } else {
        setError('Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setActionLoading({ ...actionLoading, [id]: null });
    }
  };

  // Filter testimonials
  const filteredTestimonials = testimonials.filter(t => {
    if (filter === 'all') return true;
    return t.status === filter;
  });

  // Status badge component
  const StatusBadge = ({ status }) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      approved: 'bg-green-500/20 text-green-400 border-green-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    const labels = {
      pending: 'En attente',
      approved: 'Validé',
      rejected: 'Refusé'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colors[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-gray-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Gestion des Témoignages
        </h2>
        <p className="text-gray-400">Validez, refusez ou supprimez les témoignages utilisateurs</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-white/10"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total</p>
              <p className="text-3xl font-bold text-white">{stats.total}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-yellow-500/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">En attente</p>
              <p className="text-3xl font-bold text-yellow-400">{stats.pending}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-green-500/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Validés</p>
              <p className="text-3xl font-bold text-green-400">{stats.approved}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 backdrop-blur-sm rounded-lg p-6 border border-red-500/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Refusés</p>
              <p className="text-3xl font-bold text-red-400">{stats.rejected}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
        </motion.div>
      </div>

      {/* Filter */}
      <div className="flex items-center space-x-2">
        <Filter className="w-5 h-5 text-gray-400" />
        <span className="text-gray-400 text-sm">Filtrer:</span>
        <div className="flex space-x-2">
          {['all', 'pending', 'approved', 'rejected'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === f
                  ? 'bg-[#4A90E2] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {f === 'all' ? 'Tous' : f === 'pending' ? 'En attente' : f === 'approved' ? 'Validés' : 'Refusés'}
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Testimonials Table */}
      <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5 border-b border-white/10">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Auteur
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Poste / Entreprise
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Note
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Témoignage
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredTestimonials.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                    Aucun témoignage trouvé
                  </td>
                </tr>
              ) : (
                filteredTestimonials.map((testimonial) => (
                  <tr key={testimonial._id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="text-white font-medium">{testimonial.user_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <p className="text-white">{testimonial.role}</p>
                        {testimonial.company && (
                          <p className="text-gray-400">{testimonial.company} ({testimonial.company_size})</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <p className="text-gray-300 text-sm line-clamp-2">
                        {testimonial.message}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={testimonial.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {testimonial.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(testimonial._id)}
                              disabled={actionLoading[testimonial._id] === 'approving'}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-all disabled:opacity-50"
                              title="Valider"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleReject(testimonial._id)}
                              disabled={actionLoading[testimonial._id] === 'rejecting'}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all disabled:opacity-50"
                              title="Refuser"
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {testimonial.status === 'approved' && (
                          <button
                            onClick={() => handleReject(testimonial._id)}
                            disabled={actionLoading[testimonial._id] === 'rejecting'}
                            className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-all disabled:opacity-50"
                            title="Refuser"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        )}
                        {testimonial.status === 'rejected' && (
                          <button
                            onClick={() => handleApprove(testimonial._id)}
                            disabled={actionLoading[testimonial._id] === 'approving'}
                            className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-all disabled:opacity-50"
                            title="Valider"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(testimonial._id)}
                          disabled={actionLoading[testimonial._id] === 'deleting'}
                          className="p-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-400 rounded-lg transition-all disabled:opacity-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsManagement;
